import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import type { LegalConfig } from '../../../lib/types';

/** Fallback values used when the super-admin hasn't filled in the config yet. */
const DEFAULTS = {
  legalEntityName: '[Your Legal Entity Name]',
  companyType: 'Sole trader',
  vatNumber: '[VAT MT…]',
  registrationNumber: '',
  supportEmail: 'support@sieveguard.com',
  privacyEmail: 'privacy@sieveguard.com',
  websiteUrl: 'https://sieveguard.com',
  governingLawJurisdiction: 'Republic of Malta',
  dataResidencyRegion: 'AWS Frankfurt (eu-central-1)',
  retentionDaysAfterTermination: 30,
  registeredAddressLine: '[Registered address — set in Admin → Settings → Legal]',
  lastUpdatedAt: '2026-06-12',
};

/**
 * Loads the live legal config and returns a fully-populated record (placeholders fall back
 * to clearly-marked defaults). Use in every legal page to interpolate company-specific
 * values into the templated clauses.
 *
 * When the super-admin has flipped `hideBusinessIdentityInFooter` on (Maltese
 * sole-trader mode — see Admin → Settings → Public site), personal identity
 * fields are substituted so legal pages read as if SieveGuard the product were
 * the contracting party rather than "Stephen Agius — Sole Trader, VAT MT…":
 *
 *   - legalEntityName        → 'SieveGuard' (the trading name)
 *   - companyType            → '' (drops parenthetical detail like "Sole trader")
 *   - vatNumber              → '' (drops VAT number)
 *   - registrationNumber     → '' (drops company reg number)
 *   - registeredAddressLine  → '' (drops the physical address)
 *   - legalEntityCountry     → null (Terms VAT clause falls back to generic wording)
 *
 * Every legal page reads a `hideBusinessIdentity` boolean from this hook and
 * skips the `({companyType}, VAT {vatNumber}, registered at {address})` block
 * when it's true — so we don't render an ugly ", , " gap where the details
 * used to be.
 *
 * Support / privacy contact emails, sub-processor list, retention window, and
 * governing-law jurisdiction are NOT substituted — those are still legally
 * required to be reachable + accurate even if the contracting party is
 * anonymised behind a brand name.
 */
export function useLegal() {
  const { data } = useQuery({
    queryKey: ['legal-config'],
    queryFn: () => api<LegalConfig>('/legal/config', { auth: false }),
    staleTime: 60 * 60 * 1000,
  });
  const c = data ?? {};
  const a = c.registeredAddress;
  const hide = Boolean(c.hideBusinessIdentityInFooter);
  const addressLine = a
    ? [a.street, a.city, a.postalCode, a.country].filter(Boolean).join(', ') ||
      DEFAULTS.registeredAddressLine
    : DEFAULTS.registeredAddressLine;
  return {
    /**
     * True when the operator has enabled Maltese-sole-trader-style anonymisation.
     * Each legal page uses this to skip the parenthetical business-identity block
     * ("(Sole trader, VAT MT…, registered at …)") instead of rendering it empty.
     */
    hideBusinessIdentity: hide,
    legalEntityName: hide ? 'SieveGuard' : c.legalEntityName || DEFAULTS.legalEntityName,
    companyType: hide ? '' : c.companyType || DEFAULTS.companyType,
    vatNumber: hide ? '' : c.vatNumber || DEFAULTS.vatNumber,
    registrationNumber: hide ? '' : c.registrationNumber || DEFAULTS.registrationNumber,
    supportEmail: c.supportEmail || DEFAULTS.supportEmail,
    privacyEmail: c.privacyEmail || DEFAULTS.privacyEmail,
    websiteUrl: c.websiteUrl || DEFAULTS.websiteUrl,
    governingLawJurisdiction: c.governingLawJurisdiction || DEFAULTS.governingLawJurisdiction,
    dataResidencyRegion: c.dataResidencyRegion || DEFAULTS.dataResidencyRegion,
    retentionDaysAfterTermination:
      c.retentionDaysAfterTermination ?? DEFAULTS.retentionDaysAfterTermination,
    registeredAddressLine: hide ? '' : addressLine,
    /** ISO 3166-1 alpha-2 of the seller's legal home (used by the Terms VAT clause). */
    legalEntityCountry: hide ? null : a?.country || null,
    subProcessors: c.subProcessors ?? [],
    lastUpdatedAt: c.lastUpdatedAt || DEFAULTS.lastUpdatedAt,
    /** Raw social map (undefined = unset). Render via socialLinkList() which applies the default. */
    socialLinks: c.socialLinks,
    /** Super-admin toggle for the /about page (undefined/true = shown). */
    aboutPageEnabled: c.aboutPageEnabled !== false,
  };
}
